<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'fal fa-hospital'"
      :section-title="$tc('configuration.sections.center', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteCenters(selectedRows)"
        @shortkey="deleteCenters(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCentersModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showCentersModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCentersModal($t('configuration.actions.add'))"
        @shortkey="showCentersModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="centersModalWrapper">
      <centers-configuration-modal
        ref="centersModal"
        :action-title="actionTitle"
        @update="patchCenters($event)"
        @create="createCenter($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsCentersGrid from '@/modules/configurationModule/detail/centersAndServices/centers/columnsCentersGrid'
import CentersConfigurationModal from '@/modules/configurationModule/detail/centersAndServices/centers/CentersConfigurationModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import ConfigurationCenterServices
    from '@/api/resources/configuration/centersAndAreas/center/ConfigurationCenterServices'

export default {
    name: 'CentersConfigurationGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        CentersConfigurationModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.centersAndServices.centers',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsCentersGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        hasActivatedCenter() {
            return !!this.selectedRows.find(center => center.active)
        },
        hasDeactivatedCenter() {
            return !!this.selectedRows.find(center => !center.active)
        },
        hasAllActivated() {
            return (this.selectedRows.length === this.selectedRows.filter(center => center.active).length)
        },
        hasAllDeactivated() {
            return (this.selectedRows.length === this.selectedRows.filter(center => !center.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActivatedCenter && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivatedCenter && !this.hasAllActivated
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const centersToPatch = selectionBatch.filter(center => center.active !== option).map(center => {
                return {
                    active: option,
                    id: center.id
                }
            })
            if (centersToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(ConfigurationCenterServices.patchCentersBatch(centersToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: centersToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchCenters(...centers) {
            const request = centers.map(center => this.mapCenterToRequest(center))
            this.updateLoading(true)
            const [err, data] = await this.toCall(ConfigurationCenterServices.patchCentersBatch(request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createCenter(center) {
            const request = this.mapCenterToRequest(center)
            this.updateLoading(true)
            const [err] = await this.toCall(ConfigurationCenterServices.createCenter(request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
            this.$refs.centersModal.hide()
            this.reloadData()
        },
        async deleteCentersBatch(centers) {
            const centersIds = centers.map(center => center.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(ConfigurationCenterServices.deleteCentersBatch(centersIds))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (centersIds.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: centersIds.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        async deleteCenters(centers) {
            if (centers.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteCentersBatch(centers)
                }
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(ConfigurationCenterServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showCentersModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteCenters(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(ConfigurationCenterServices))
        },
        showCentersModal(title, selectedCenter) {
            this.actionTitle = title
            this.$refs.centersModalWrapper.show([selectedCenter])
        },
        mapCenterToRequest(center) {
            return {
                id: center.id,
                active: center.active,
                address: {
                    address: center.address?.address,
                    countryId: center.address?.country?.id,
                    municipalityId: center.address?.municipality?.id,
                    notes: center.address?.notes,
                    postalCode: center.address?.postalCode,
                    provinceId: center.address?.province?.id
                },
                code: center.code,
                contactInformation: center.contactInformation,
                healthManagementAreaId: center.healthManagementArea?.id ?? null,
                languageId: center.language?.id,
                name: center.name
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
